import React from 'react';
import { Building2, Calendar, TrendingDown, Percent, FileText, Calculator, Banknote } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета амортизации
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Функция для получения названия метода амортизации
  const getDepreciationMethodName = (method) => {
    const methodNames = {
      'straight_line': 'Линейный метод',
      'reducing_balance': 'Метод уменьшаемого остатка',
      'sum_of_years': 'По сумме чисел лет',
      'units_of_production': 'Пропорционально объему продукции'
    };
    return methodNames[method] || method;
  };

  // Функция для получения названия налогового метода
  const getTaxMethodName = (method) => {
    return method === 'linear' ? 'Линейный метод' : 'Нелинейный метод';
  };

  return (
    <ToolResult title="Результаты расчета амортизации">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Годовая амортизация */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Calendar className="w-4 h-4"/>
            Среднегодовая амортизация
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.averageAnnualDepreciation)}
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              ({formatNumber(results.averageDepreciationRate, 2)}% в год)
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            В месяц: {formatCurrency(results.averageMonthlyDepreciation)}
          </p>
        </div>

        {/* Общая сумма амортизации */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <TrendingDown className="w-4 h-4"/>
            Общая сумма амортизации
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatCurrency(results.totalDepreciation)}
            </span>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              за {results.usefulLife} {formatYearsLabel(results.usefulLife)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            От первоначальной стоимости: {formatNumber((results.totalDepreciation / results.assetCost) * 100, 2)}%
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Название актива" 
          value={results.assetName} 
          color="indigo" 
          icon={<Building2 className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Первоначальная стоимость" 
          value={formatCurrency(results.assetCost)} 
          color="green" 
          icon={<Banknote className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Ликвидационная стоимость" 
          value={formatCurrency(results.salvageValue)} 
        />
        
        <ResultItem 
          label="Амортизируемая стоимость" 
          value={formatCurrency(results.depreciableAmount)} 
          color="orange" 
        />
        
        <ResultItem 
          label="Срок полезного использования" 
          value={`${results.usefulLife} ${formatYearsLabel(results.usefulLife)}`} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Метод амортизации" 
          value={getDepreciationMethodName(results.depreciationMethod)} 
          icon={<Calculator className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Средняя норма амортизации" 
          value={`${formatNumber(results.averageDepreciationRate, 2)}% в год`} 
          color="indigo"
          icon={<Percent className="w-4 h-4"/>} 
        />

        {results.wasRevalued && (
          <ResultItem 
            label="Переоценка актива" 
            value={`${formData.revaluationType === 'increase' ? '+' : '-'}${formatCurrency(results.revaluationAmount)} в ${results.revaluationYear} год`} 
            color="blue" 
          />
        )}
        
        {formData.includeTaxCalculation && (
          <>
            <ResultItem 
              label="Метод налогового учета" 
              value={getTaxMethodName(formData.taxMethod)} 
              icon={<FileText className="w-4 h-4"/>} 
            />
            
            <ResultItem 
              label="Амортизация для налогового учета" 
              value={formatCurrency(results.totalTaxDepreciation)} 
            />
            
            <ResultItem 
              label="Разница (бухгалтерский - налоговый)" 
              value={formatCurrency(results.accountingTaxDifference)} 
              color={results.accountingTaxDifference >= 0 ? 'green' : 'red'} 
            />
          </>
        )}
      </div>

      {/* Информация о временных разницах */}
      {formData.includeTaxCalculation && Math.abs(results.accountingTaxDifference) > 0.01 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mt-2 border border-blue-200 dark:border-blue-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Временные разницы</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {results.accountingTaxDifference > 0 ? (
                  <>
                    Накопленная вычитаемая временная разница за период составляет <span className="font-medium text-green-600 dark:text-green-400">{formatCurrency(results.accountingTaxDifference)}</span>. 
                    Это приведет к формированию отложенного налогового актива (ОНА) в размере <span className="font-medium text-green-600 dark:text-green-400">{formatCurrency(results.accountingTaxDifference * 0.2)}</span> 
                    (при ставке налога на прибыль 20%).
                  </>
                ) : (
                  <>
                    Накопленная налогооблагаемая временная разница за период составляет <span className="font-medium text-red-600 dark:text-red-400">{formatCurrency(Math.abs(results.accountingTaxDifference))}</span>. 
                    Это приведет к формированию отложенного налогового обязательства (ОНО) в размере <span className="font-medium text-red-600 dark:text-red-400">{formatCurrency(Math.abs(results.accountingTaxDifference) * 0.2)}</span> 
                    (при ставке налога на прибыль 20%).
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;